import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, ApiError, Button, ErrorAlert, Skeleton, useDocumentTitle } from "@unizpay/ui";
import { UsersRound } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { RoleBadge } from "../components/RoleBadge";
import { api } from "../lib/api";
import { ROLE_DESCRIPTIONS } from "../lib/permissions";
import { rememberProfile } from "../lib/profile";
import { ME_KEY, useMe, useSignOut } from "../lib/queries";
import { setReturnTo } from "../lib/returnTo";
import type { InvitationPreview } from "../lib/types";
import { AuthShell } from "./LoginPage";

const linkButtonClass =
  "mt-6 inline-flex h-10 w-full items-center justify-center rounded-md bg-accent px-4 text-sm font-medium text-accent-fg shadow-sm hover:bg-accent-hover";

function Loading({ label }: { label: string }) {
  return (
    <output className="block" aria-label={label}>
      <Skeleton className="h-6 w-2/3" />
      <Skeleton className="mt-4 h-4 w-full" />
      <Skeleton className="mt-2 h-4 w-3/4" />
      <Skeleton className="mt-6 h-10 w-full" />
    </output>
  );
}

/** Accept (signed in) or sign in first, then come back here (signed out). */
function AcceptAction({ token, invitation }: { token: string; invitation: InvitationPreview }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useMe();
  const signOut = useSignOut();
  const here = `/invite/${encodeURIComponent(token)}`;

  const accept = useMutation({
    mutationFn: () => api.account.acceptInvitation(token),
    onSuccess: async ({ profile_id }) => {
      rememberProfile(profile_id);
      await queryClient.invalidateQueries({ queryKey: ME_KEY });
      navigate("/dashboard", { replace: true });
    },
  });

  if (me.isPending) return <Skeleton className="mt-6 h-10 w-full" />;

  const signedOut = me.error instanceof ApiError && me.error.status === 401;
  if (signedOut) {
    return (
      <>
        <Button
          size="lg"
          className="mt-6 w-full"
          onClick={() => {
            setReturnTo(here);
            navigate("/login");
          }}
        >
          Sign in to accept
        </Button>
        <p className="mt-3 text-center text-xs text-fg-muted">
          Sign in with <span className="font-medium text-fg">{invitation.email}</span>. You will come back
          here to accept.
        </p>
      </>
    );
  }
  if (me.isError) return <ErrorAlert error={me.error} title="Could not check your sign-in" />;

  const wrongEmail = accept.error instanceof ApiError && accept.error.status === 400;
  return (
    <>
      {accept.error && (
        <Alert
          tone="danger"
          title={wrongEmail ? "Signed in with a different email" : undefined}
          className="mt-6"
        >
          {accept.error.message}
        </Alert>
      )}
      {wrongEmail ? (
        <Button
          size="lg"
          className="mt-6 w-full"
          loading={signOut.isPending}
          onClick={() => {
            setReturnTo(here);
            signOut.mutate();
          }}
        >
          Sign out and switch account
        </Button>
      ) : (
        <Button size="lg" className="mt-6 w-full" loading={accept.isPending} onClick={() => accept.mutate()}>
          Accept invitation
        </Button>
      )}
      <p className="mt-3 text-center text-xs text-fg-muted">
        Signed in as <span className="font-medium text-fg">{me.data.user.email}</span>
      </p>
    </>
  );
}

/** /invite/:token, the link in an invitation email. Outside the app shell, like sign-in. */
export function InvitePage() {
  useDocumentTitle("Invitation");
  const { token = "" } = useParams();
  const invitation = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => api.account.invitation(token),
    retry: false,
  });

  if (invitation.isPending) {
    return (
      <AuthShell>
        <Loading label="Loading invitation" />
      </AuthShell>
    );
  }

  if (invitation.isError) {
    const invalid = invitation.error instanceof ApiError && invitation.error.status === 404;
    return (
      <AuthShell>
        <h1 className="text-lg font-semibold">
          {invalid ? "This invitation is no longer valid" : "Could not load the invitation"}
        </h1>
        {invalid ? (
          <p className="mt-1.5 text-[0.8125rem] text-fg-muted">
            It may have expired (invitations last 7 days), been revoked, or already been accepted. Ask the
            person who invited you to send a new one.
          </p>
        ) : (
          <div className="mt-4">
            <ErrorAlert error={invitation.error} />
          </div>
        )}
        <Link to="/dashboard" className={linkButtonClass}>
          Go to the dashboard
        </Link>
      </AuthShell>
    );
  }

  const inv = invitation.data;
  return (
    <AuthShell>
      <span className="flex size-10 items-center justify-center rounded-full bg-accent/10 text-accent">
        <UsersRound aria-hidden="true" className="size-5" />
      </span>
      <h1 className="mt-4 text-lg font-semibold">Join {inv.profile_name}</h1>
      <p className="mt-1.5 text-[0.8125rem] text-fg-muted">
        You have been invited to <span className="font-medium text-fg">{inv.profile_name}</span> on UnizPay.
      </p>
      <div className="mt-4 rounded-md border border-line px-3 py-2.5 text-[0.8125rem]">
        <p className="flex items-center gap-2">
          <span className="text-fg-muted">Role</span>
          <RoleBadge role={inv.role} />
        </p>
        <p className="mt-1 text-xs text-fg-muted">{ROLE_DESCRIPTIONS[inv.role]}</p>
      </div>
      <AcceptAction token={token} invitation={inv} />
    </AuthShell>
  );
}
